import { Box, Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Typography } from 'packages/common-frontend/components/Typography';
import { z } from 'zod/v4';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { LoanProductSelect } from '~/packages/common-frontend/components/LoanProductSelect';
import { ReceivablesTable } from '~/packages/loanProduct/ui/receivablesDashboard/ReceivablesTable';
import { PortfolioTemplate } from '~/packages/portfolio/ui/shared/PortfolioTemplate';

// Types
const RouteParamsStruct = z.object({
  creditFacility: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const [queryParams] = useSearchParams();
  const { creditFacility: creditFacilityId } = RouteParamsStruct.parse(
    Object.fromEntries(queryParams),
  );
  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);

  const [loanProductId, setLoanProductId] = useState<string>('all');

  const [receivablesCount, setReceivablesCount] = useState<number | null>(null);

  return (
    <PortfolioTemplate
      creditFacilityId={creditFacility.id}
      creditFacilityName={creditFacility.name}
      selectedTab="receivables"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box w="50%">
          <LoanProductSelect
            creditFacilityId={creditFacility.id}
            loanProductId={loanProductId}
            setLoanProductId={setLoanProductId}
            useAllOption
          />
        </Box>
        {receivablesCount !== null && (
          <Typography variant="section-subtitle" fontWeight="bold">
            Receivable Count: {receivablesCount}
          </Typography>
        )}
      </Flex>

      <ReceivablesTable
        organizationId={user.organization.id}
        creditFacilitySlug={creditFacility.slug}
        creditFacilityId={creditFacility.id}
        loanProductId={loanProductId}
        implementationId={creditFacility.implementationId}
        setReceivablesCount={setReceivablesCount}
      />
    </PortfolioTemplate>
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/portfolio/overview/facilities" />;
      }}
    >
      <Box width="100%" padding="2.5rem">
        <RouteBase />
      </Box>
    </RouteErrorBoundary>
  );
};
